import Signature from './AbstractSignature';
import { CreateElement, VNode } from 'vue';
import { ChildParams, MethodAttributes, referencesMethod } from '@/templates/References';

export default function ObjcSignatureFactory(kind: string) {
  switch (kind) {
    case 'method':
      return ObjcMethodSignature;
    case 'prop':
      return ObjcPropertySignature;
    case 'typedef':
      return ObjcTypedefSignature;
    default:
      return {
        getSignature(_method: any): null {
          return null;
        },
      };
  }
}

class ObjcMethodSignature extends Signature {
  public static LANG = 'objc';

  public static getSignature(method: referencesMethod): (h: CreateElement) => VNode {
    const attrStatic: string = this.attrStatic(method.attributes);
    const paramSignatures = this.paramSignatures(this.getParamNames(method.title), method.params);
    const returnsType = this.returnsType(method.returns);

    return (h) =>
      h('code', [
        h('span', { class: 'token punctuation' }, `${attrStatic} `),
        h('span', { class: 'token punctuation' }, '('),
        returnsType(h),
        h('span', { class: 'token punctuation' }, ')'),
        paramSignatures(h),
      ]);
  }

  private static attrStatic(attributes: MethodAttributes): string {
    return attributes && attributes.static ? '+' : '-';
  }

  /**
   * Имена параметров в title записаны через ':' создаем из них массив
   *
   * @param title тайтл метода method.title
   * @return Array массив содержащий имена параметров
   */
  private static getParamNames(title: string): string[] {
    return title.split(':').filter((str) => str);
  }

  /**
   * Проходимся по массиву имен параметров,
   * каждому сопоставляем параметр из массива params и записываем в строку следующего вида:
   * deviceWithType:(VIAudioDeviceType)type
   *
   * @param paramNames массив содержащий имена параметров созданный на основе method.title
   * @param paramSignatures массив содержащий параметры метода заданные в method.params
   * @return Function возвращает рендер функцию, которая создает часть сигнатуры
   */
  private static paramSignatures(
    paramNames: string[],
    paramSignatures: ChildParams[]
  ): (h: CreateElement) => VNode[] {
    const leftpad: number = this.getSecondParameterPad(paramNames);
    return (h) =>
      paramNames.map((paramName: string, idx: number) => {
        return !paramSignatures
          ? h('span', `${paramName}`)
          : h('span', [
              idx === 0 ? `${paramName}` : this.leftpad(`${paramName}`, leftpad),
              h('span', { class: 'token punctuation' }, ':'),
              h('span', { class: 'token punctuation' }, '('),
              h('span', {
                class: 'token md-parse',
                domProps: {
                  innerHTML: `${
                    paramSignatures[idx].types
                      ? this.convertToHtml(<string>paramSignatures[idx].types[0])
                      : ''
                  }`,
                },
              }),
              h('span', { class: 'token punctuation' }, ')'),
              `${paramSignatures[idx].title} `,
              !(idx === paramNames.length - 1) ? h('br') : '',
            ]);
      });
  }

  private static getSecondParameterPad(paramIndexes: string[]): number {
    if (paramIndexes.length < 2) return 0;
    return paramIndexes[0].length + 8 > paramIndexes[1].length
      ? paramIndexes[0].length + 8
      : paramIndexes[1].length + 4;
  }

  /**
   * Добавляет пробелы слева, добавляя до определённой длины строки
   *
   * @param str строка,
   * @param len итоговая длина
   * @return строка заданной длины
   */
  private static leftpad(str: string, len: number): string {
    const ch = ' ';
    let i = -1;

    len = len - str.length;
    while (++i < len) {
      str = ch + str;
    }
    return str;
  }
}

class ObjcPropertySignature extends Signature {
  public static LANG = 'objc';

  //TODO сделать тип для property
  public static getSignature(property: any): (h: CreateElement) => VNode {
    const title: string = property.title;
    const returnsType = this.returnsType(property.returns);
    const attributeSignatures = this.attributeSignatures(property.attributes);

    return (h) =>
      h('code', [
        h('span', { class: 'token function' }, '@property '),
        h('span', { class: 'token punctuation' }, '('),
        attributeSignatures(h),
        h('span', { class: 'token punctuation' }, ') '),
        returnsType(h),
        h('span', { class: 'token keyword' }, `${title}`),
      ]);
  }

  public static returnsType(returnTypes: Array<string>): (h: CreateElement) => VNode {
    if (!returnTypes || !returnTypes.length) return (h) => h();

    return (h) =>
      h(
        'span',
        returnTypes.map((item) => {
          return h('span', { class: 'token md-parse type' }, [
            h('span', {
              domProps: { innerHTML: `${this.convertToHtml(item)}` },
            }),
            !(item.slice(-1) === '*') ? ' ' : '',
          ]);
        })
      );
  }

  /**
   * Собираем имена аттрибутов в строку, через ', ',
   * если есть getter записываем его в строку в виде:
   * getter = isVideoEnabled
   *
   * @param attributes аттрибуты заданные в method.attributes
   * @return Function возвращает рендер функцию, которая создает часть сигнатуры
   */
  private static attributeSignatures(attributes: any): (h: CreateElement) => VNode[] {
    if (!attributes) return (h) => [h()];

    const arr: string[] = Object.keys(attributes).filter((attr) =>
      ['nonatomic', 'strong', 'readonly', 'nullable', 'weak', 'assign', 'copy'].includes(attr)
    );
    if (attributes.getter) arr.push(`getter = ${attributes.getter}`);

    return (h) =>
      arr.map((val: string, idx: number) => {
        return h('span', [
          h('span', { class: 'token attr' }, `${val}`),
          !(idx === arr.length - 1) ? h('span', { class: 'token punctuation' }, ', ') : '',
        ]);
      });
  }
}

class ObjcTypedefSignature extends Signature {
  public static LANG = 'objc';

  public static getSignature(typedef: referencesMethod): (h: CreateElement) => VNode {
    const paramSignatures = this.paramSignatures(typedef.params);
    const returnsType = this.returnsType(typedef.returns);

    return (h) =>
      h('code', [
        h('span', { class: 'token punctuation' }, 'typedef '),
        returnsType(h),
        h('span', { class: 'token punctuation' }, ' ('),
        h('span', { class: 'token punctuation' }, '^'),
        `${typedef.title}`,
        h('span', { class: 'token punctuation' }, ')'),
        h('span', { class: 'token punctuation' }, '('),
        paramSignatures(h),
        h('span', { class: 'token punctuation' }, ')'),
      ]);
  }

  private static paramSignatures(paramSignatures: ChildParams[]): (h: CreateElement) => VNode {
    if (!paramSignatures) return (h) => h();

    return (h) =>
      h(
        'span',
        paramSignatures.map((val: ChildParams, idx: number) => {
          return h('span', [
            val.types ? this.returnsType(val.types)(h) : '',
            ` ${val.title}`,
            !(idx === paramSignatures.length - 1)
              ? h('span', { class: 'token punctuation' }, ', ')
              : '',
          ]);
        })
      );
  }
}
